import {SecondaryOpeningDialog} from "../components/SecondaryOpeningDialog";
import {Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, FormGroup, IconButton, Menu, MenuItem, Paper, Popover, Tab, Tabs, Tooltip, Typography} from "@mui/material";
import {Close, DoneAll, Home, MoreHoriz, Redo, Save, Undo} from "@mui/icons-material";
import {useCallback, useRef, useState} from "react";
import {useAsyncAction} from "nate-react-api-helpers";
import {useConfirm} from "../../../../../misc/Confirm";
import {EventEmitter2} from "../util/EventEmitter2";
import {ElevationApp, ElevationSelection, unionSelection, workspaceSelection} from "../ElevationApp";
import {MdMenuTab, MdSelection, selectionGroupOf, Workspace, workspaceHomeView} from "../model/view/MdViewSettings";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {SaveElevationDialog} from "../components/SaveElevationDialog";
import {GlobalDoorSaveDialog} from "../components/GlobalDoorSaveDialog";
import {DimensionChange, HingeGroupSplit, SavePreflight, SharedElevations} from "../../../../../api/elevationTool/ThreePartElevation";
import {ConfirmCloseDialog} from "../components/ConfirmCloseDialog";
import {HardwareReconcileDialog} from "../components/HardwareReconcileDialog";
import {ShowError} from "../components/ShowError";
import {LegacyTab} from "./LegacyTab";
import {WorkspaceTab} from "./WorkspaceTab";
import {DebugTab} from "./DebugTab";
import {SelectedTab} from "./SelectedTab";
import {StickPickTab} from "./StickPickTab";
import {AnchorSetupTab, anchorSetupAllowed, endAnchorSetup, startAnchorSetup} from "./AnchorSetupTab";
import {cancelPick} from "./selectedTab/helpers";
import {findStickById, pieceStickId} from "../scenes/frameGrid/BlockOps";
import {useMenubarShortcuts} from "./useMenubarShortcuts";
import {useUndoRedoShortcuts} from "./useUndoRedoShortcuts";
import {useLeaveGuard} from "./useLeaveGuard";

// the top-bar accent (background) for each workspace; the foreground is white so the
// icons/text/controls read against it.
export const WORKSPACE_THEME: {[k in Workspace]: string} = {
    screen: "#512da8",   // deep-purple 700 (blue is over-used elsewhere)
    door: "#00695c",     // teal 800
    hardware: "#d84315", // deep-orange 800
};

// the tab strip's values: one tab per workspace, the Anchors tab (anchor setup), the
// contextual Selected tab, and the Debug / Legacy tabs opened from the "more" menu.
type RibbonTab = Workspace | "anchors" | Exclude<MdMenuTab, "workspace">;

const TABS_SX = {minHeight: 36, "& .MuiTab-root": {minHeight: 36, py: 0, color: "rgba(255,255,255,0.7)"}, "& .Mui-selected": {color: "#fff !important"}, "& .MuiTabs-indicator": {backgroundColor: "#fff"}};

// the contextual tabs (Selected XX, a stick pick) get an accent band, MS-Office style.
const CONTEXT_TAB_SX = {
    bgcolor: "rgba(255,255,255,0.18)",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    "&.Mui-selected": {fontWeight: 600},
};

export function MenuBar(props: {
    app: EventEmitter2<ElevationApp | null>;
    onBack?: () => void;
    workspace: Workspace;
    onWorkspaceChange(workspace: Workspace): void;
}) {
    const app = useEventEmitter2(props.app);
    const ready = useEventEmitter2(app?.ready);
    const activeTab = useEventEmitter2(app?.viewSettings.activeTab);
    const selection = useEventEmitter2(app?.viewSettings.selection);
    // while an action (Split By, Join) waits for a stick click, the ribbon shows only
    // the pick's tab.
    const pick = useEventEmitter2(app?.viewSettings.stickPick);
    const picking = !!pick;
    // while the user sets up frame anchors, the ribbon shows only the Setup Anchors tab.
    const anchorSetup = useEventEmitter2(app?.viewSettings.anchorSetup);
    const dirty = useEventEmitter2(app?.dirty);
    const elevationNames = useEventEmitter2(app?.elevationNames);
    const canUndo = useEventEmitter2(app?.history.canUndo);
    const canRedo = useEventEmitter2(app?.history.canRedo);
    // the pending save prompt: what the preflight said needs confirming, which elevations the
    // save will write, whether the editor closes afterwards, and whether it updates all linked
    // openings without asking. null when no prompt is open.
    const [saving, setSaving] = useState<null | {preflight: SavePreflight; which: ElevationSelection; intent: "save" | "close"; updateAll: boolean}>(null);
    // the pending-changes prompt, with where the editor goes once the user has saved or
    // discarded (null = the prompt is closed). The exit is the Close Editor button's
    // onBack, or the link / history step the leave guard intercepted.
    const [closing, setClosing] = useState<null | {exit: () => void}>(null);
    // the exit a save-then-close finishes with, held while the save runs.
    const pendingExit = useRef<(() => void) | null>(null);

    // leave the editor: the exit the pending-changes prompt was opened for, else onBack.
    const leave = useCallback(() => {
        const exit = pendingExit.current;
        pendingExit.current = null;
        if(exit) exit();
        else props.onBack?.();
    }, [props.onBack]);
    // the pending edit-global save prompt, and whether the editor closes afterwards. null when closed.
    const [globalSaving, setGlobalSaving] = useState<null | {thenClose: boolean}>(null);
    // anchor for the save popover (the per-elevation checkboxes). null when closed.
    const [saveMenuAnchor, setSaveMenuAnchor] = useState<HTMLElement | null>(null);
    // true when the save popover was opened from "Save all".
    const [saveMenuUpdateAll, setSaveMenuUpdateAll] = useState(false);
    // the save popover's checkbox state.
    const [saveSelection, setSaveSelection] = useState<ElevationSelection>({screen: true, door: true, hardware: true});
    // anchor for the "more" menu (Debug / Legacy). null when closed.
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    // the hardware-group reconcile report from the last load, and the one already acknowledged
    // — so the review dialog shows once per distinct load (a new load emits a new report object).
    const hardwareReconcile = useEventEmitter2(app?.hardwareReconcile);
    // set when this opening fills a door slot on another opening's frame.
    const secondaryOf = useEventEmitter2(app?.secondaryOf);
    const workspace = useEventEmitter2(app?.viewSettings.workspace);
    const [ackedReconcile, setAckedReconcile] = useState<typeof hardwareReconcile>(null);

    // kick off a save of the given elevations: ask the server what it would need confirmed
    // first, so a name, a shared-elevation fan-out and a nominal-dimension write-back land on
    // one prompt. Nothing to confirm => save straight away. updateAll skips the linked-openings
    // question and updates every opening that shares the elevations.
    const confirm = useConfirm();

    const save = useAsyncAction(async (input: {which: ElevationSelection; thenClose?: boolean; updateAll?: boolean}) => {
        if(!app) return;

        // door slot names can't change once saved, so the first save of a slot asks to check them.
        const newSlots = input.which.screen && !app.secondaryOf.value ? app.newSlots() : [];
        if(newSlots.length > 0) {
            try {
                await confirm.confirm(<>
                    Door slot names can't be changed after this save. Check them now:
                    <ul>
                        {newSlots.map(s => <li key={s.name}>{s.name} - {s.opening ?? "no opening yet"}</li>)}
                    </ul>
                </>);
            } catch {
                return; // cancelled
            }
        }

        const intent = input.thenClose ? "close" : "save";
        const updateAll = !!input.updateAll;
        let preflight = await app.preflight({name: app.defaultElevationName(), which: input.which});
        if(updateAll) preflight = {...preflight, sharedElevations: null, sharedIdentityConflict: null};

        if(app.needsName(input.which) || needsPrompt(preflight)) {
            setSaving({preflight, which: input.which, intent, updateAll});
            return;
        }

        // the save runs the same checks itself, so anything that landed since the preflight
        // still comes back here — open the prompt on it rather than saving half of it.
        const res = await app.save({which: input.which, identityResolution: updateAll ? "update-all" : undefined});
        if(needsPrompt(res)) {
            setSaving({preflight: asPreflight(res), which: input.which, intent, updateAll});
            return;
        }

        // rendering happens in the background: the save requeues the affected openings'
        // content-addressed renderings server-side (see core/opening/drw resolveRendering).
        // An edit made while the save ran stays unsaved, so the tool stays open for it.
        if(input.thenClose && !app.hasUnsavedChangesIn(input.which)) leave();
    }, [app, leave]);

    // false once the user dismisses the save error dialog; reset on the next save.
    const [showSaveError, setShowSaveError] = useState(true);

    const startSave = useCallback((which: ElevationSelection, thenClose = false, updateAll = false) => {
        setShowSaveError(true);
        save.callback({which, thenClose, updateAll});
    }, [save]);

    const hasSelection = !!selection && selection.length > 0;

    // Escape clears the current canvas selection. The per-element delete hotkeys
    // live in each contextual "Selected XX" tab instead (see selectedTab/*).
    useMenubarShortcuts(picking ? [
        {keys: ["Escape"], display: "Esc", hint: "Press Esc to cancel", run: () => app && cancelPick(app)},
    ] : anchorSetup && hasSelection ? [
        {keys: ["Escape"], display: "Esc", hint: "Press Esc to clear the selection", run: () => app?.viewSettings.clearSelection()},
    ] : anchorSetup ? [
        {keys: ["Escape"], display: "Esc", hint: "Press Esc to finish the anchor setup", run: () => app && endAnchorSetup(app)},
    ] : hasSelection ? [
        {keys: ["Escape"], display: "Esc", hint: "Press Esc to clear the selection", run: () => app?.viewSettings.clearSelection()},
    ] : []);

    useUndoRedoShortcuts(app);

    // the browser Back button and in-app links get the same pending-changes prompt as
    // Close Editor; a link exits to its target once saved/discarded.
    useLeaveGuard({dirty: !!dirty, onLeave: exit => setClosing({exit: exit ?? (() => props.onBack?.())})});

    if(!app) return null;
    if(!ready) return null;

    // the contextual tab only exists while something is selected; if it was active
    // when the selection cleared, fall back to the workspace tab so the Tabs value stays valid.
    const effectiveTab: MdMenuTab = activeTab === "selected" && !hasSelection ? "workspace" : (activeTab ?? "workspace");
    const selectedLabel = hasSelection ? `Selected ${selectionTitle(app, selection!)}` : "";

    // the top bar carries the workspace's colour; the icons/text/controls are white so
    // they stay legible against it.
    const bg = WORKSPACE_THEME[props.workspace];
    const white = {color: "#fff", "&.Mui-disabled": {color: "rgba(255,255,255,0.35)"}};

    const openingName = app.opening?.name || (app.opening ? "Opening " + app.opening.id : "");

    // save the workspace's elevation directly — unless another elevation has unsaved
    // changes, in which case open the popover with those pre-checked so the user can
    // save them along.
    const openSave = (anchor: HTMLElement, updateAll: boolean) => {
        if(app.editGlobal) {
            setGlobalSaving({thenClose: false});
            return;
        }

        setSaveMenuUpdateAll(updateAll);
        const which = workspaceSelection(props.workspace);
        const merged = unionSelection(which, app.unsavedSelection());
        if(merged.screen !== which.screen || merged.door !== which.door || merged.hardware !== which.hardware) {
            setSaveSelection(merged);
            setSaveMenuAnchor(anchor);
            return;
        }
        startSave(which, false, updateAll);
    };

    // the tab strip's value: the workspace tab stands in for "workspace", and anchor
    // setup shows as the Anchors tab.
    const tabValue: RibbonTab = anchorSetup ? "anchors" : effectiveTab === "workspace" ? props.workspace : effectiveTab;

    // a global door edit is door-only, and a secondary opening's frame (and so its
    // anchors) is edited on its primary.
    const doorOnly = !!app.editGlobal;
    const anchorsDisabled = doorOnly || !anchorSetupAllowed(app);

    // the Anchors tab: anchors live on the frame grid, so land on the screen workspace's
    // frame overview first.
    const openAnchors = () => {
        if(props.workspace !== "screen") {
            app.setWorkspace("screen");
            props.onWorkspaceChange("screen");
        }
        const view = app.viewSettings.activeView.value;
        if(view !== "frame-grid" && view !== "all") app.showView("frame-grid");
        startAnchorSetup(app);
    };

    const selectTab = (value: RibbonTab) => {
        switch(value) {
            case "screen":
            case "door":
            case "hardware":
                if(anchorSetup) endAnchorSetup(app);
                app.viewSettings.activeTab.emit("workspace");
                props.onWorkspaceChange(value);
                return;
            case "anchors":
                openAnchors();
                return;
            default:
                if(anchorSetup) endAnchorSetup(app);
                app.viewSettings.activeTab.emit(value);
        }
    };

    return (
        <Paper square elevation={2} style={{zIndex: 20}}>
            <Box sx={{display: "flex", alignItems: "center", bgcolor: bg, color: "#fff"}}>
                {props.onBack && (
                    <Button
                        size="small"
                        startIcon={<Close />}
                        onClick={() => {
                            if(dirty) {
                                setClosing({exit: () => props.onBack?.()});
                                return;
                            }
                            props.onBack?.();
                        }}
                        sx={{m: 0.5, flexShrink: 0, color: "#fff"}}
                    >
                        Close Editor
                    </Button>
                )}
                <Tooltip title="Home">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!!anchorSetup}
                            onClick={() => app.showView(workspaceHomeView(props.workspace))}
                            sx={{mx: 0.5, flexShrink: 0, ...white}}
                        >
                            <Home fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {picking ? (
                    <Tabs value="pick" sx={TABS_SX}>
                        <Tab value="pick" label={pick!.title} sx={CONTEXT_TAB_SX} />
                    </Tabs>
                ) : <Tabs
                    value={tabValue}
                    onChange={(e, value: RibbonTab) => selectTab(value)}
                    sx={TABS_SX}
                >
                    <Tab value="screen" label="Screen" disabled={doorOnly} />
                    <Tab value="door" label="Door" />
                    <Tab value="hardware" label="Hardware" disabled={doorOnly} />
                    <Tab value="anchors" label="Anchors" disabled={anchorsDisabled} />
                    {hasSelection && (
                        <Tab value="selected" label={selectedLabel} sx={CONTEXT_TAB_SX} />
                    )}
                    {effectiveTab === "debug" && <Tab value="debug" label="Debug" />}
                    {effectiveTab === "legacy" && <Tab value="legacy" label="Legacy" />}
                </Tabs>}
                <Tooltip title="More">
                    <span>
                        <IconButton
                            size="small"
                            disabled={picking}
                            onClick={e => setMoreAnchor(e.currentTarget)}
                            sx={{flexShrink: 0, ...white}}
                        >
                            <MoreHoriz fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Menu
                    anchorEl={moreAnchor}
                    open={!!moreAnchor}
                    onClose={() => setMoreAnchor(null)}
                >
                    <MenuItem onClick={() => { setMoreAnchor(null); selectTab("debug"); }}>Debug</MenuItem>
                    <MenuItem onClick={() => { setMoreAnchor(null); selectTab("legacy"); }}>Legacy</MenuItem>
                </Menu>

                <Box sx={{flex: 1}} />
                <Tooltip title="Undo (Ctrl+Z)">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!canUndo || picking}
                            onClick={() => app.history.undo()}
                            sx={{flexShrink: 0, ...white}}
                        >
                            <Undo fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Redo (Ctrl+Shift+Z)">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!canRedo || picking}
                            onClick={() => app.history.redo()}
                            sx={{mr: 1, flexShrink: 0, ...white}}
                        >
                            <Redo fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", mr: 1, flexShrink: 1, minWidth: 0, lineHeight: 1.2}}>
                    <Tooltip title="Opening">
                        <Box component="span" sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            ...ellipsis,
                            color: "#fff",
                        }}>
                            {openingName}
                        </Box>
                    </Tooltip>
                    <Box sx={{display: "flex", gap: 0.75, fontSize: "0.7rem", minWidth: 0}}>
                        <ElevationName label="S" title="Screen elevation" name={elevationNames?.screen ?? null} />
                        <ElevationName label="D" title="Door elevation" name={elevationNames?.door ?? null} />
                        <ElevationName label="H" title="Hardware elevation" name={elevationNames?.hardware ?? null} />
                    </Box>
                </Box>
                {save.loading && <CircularProgress size={16} sx={{color: "#fff"}} />}
                <Button
                    size="small"
                    variant={dirty ? "contained" : "outlined"}
                    startIcon={<Save />}
                    disabled={save.loading || picking}
                    onClick={e => openSave(e.currentTarget, false)}
                    sx={{
                        m: 0.5,
                        flexShrink: 0,
                        bgcolor: dirty ? "#fff" : "transparent",
                        color: dirty ? bg : "#fff",
                        borderColor: "rgba(255,255,255,0.7)",
                        "&:hover": {bgcolor: dirty ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.12)", borderColor: "#fff"},
                    }}
                >
                    {dirty ? "Save" : "Saved"}
                </Button>
                {!app.editGlobal && <Tooltip title="Save all — update every linked opening without asking">
                    <span>
                        <IconButton
                            size="small"
                            disabled={save.loading || picking}
                            onClick={e => openSave(e.currentTarget, true)}
                            sx={{mr: 0.5, flexShrink: 0, ...white}}
                        >
                            <DoneAll fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>}
            </Box>

            <Popover
                open={!!saveMenuAnchor}
                anchorEl={saveMenuAnchor}
                onClose={() => setSaveMenuAnchor(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
            >
                <Box sx={{p: 1.5, minWidth: 200}}>
                    <Typography variant="caption" color="text.secondary">Save elevations</Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={saveSelection.screen} onChange={e => setSaveSelection(s => ({...s, screen: e.target.checked}))} />}
                            label="Frame / Screen"
                        />
                        <FormControlLabel
                            control={<Checkbox size="small" checked={saveSelection.door} onChange={e => setSaveSelection(s => ({...s, door: e.target.checked}))} />}
                            label="Door"
                        />
                        <FormControlLabel
                            control={<Checkbox size="small" checked={saveSelection.hardware} onChange={e => setSaveSelection(s => ({...s, hardware: e.target.checked}))} />}
                            label="Hardware"
                        />
                    </FormGroup>
                    <Divider sx={{my: 1}} />
                    <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Save />}
                            disabled={!saveSelection.screen && !saveSelection.door && !saveSelection.hardware}
                            onClick={() => {
                                setSaveMenuAnchor(null);
                                startSave(saveSelection, false, saveMenuUpdateAll);
                            }}
                        >
                            {saveMenuUpdateAll ? "Save all" : "Save"}
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {saving && (
                <SaveElevationDialog
                    defaultName={app.defaultElevationName()}
                    openingName={openingName}
                    elevations={app.elevationNamingList(saving.which)}
                    needsName={app.needsName(saving.which)}
                    preflight={saving.preflight}
                    onCancel={() => setSaving(null)}
                    onApply={async input => {
                        const res = await app.save({
                            name: input.name,
                            confirm: input.confirm,
                            identityResolution: saving.updateAll ? "update-all" : input.identityResolution,
                            applyDimensionsTo: input.applyDimensionsTo,
                            dimensionsConfirmed: true,
                            hingeSplitConfirmed: true,
                            slotUnlinkConfirmed: true,
                            which: saving.which,
                            renames: input.renames ?? undefined,
                        });

                        // something changed between the preflight and the save: hand the new
                        // conflict back so the same dialog re-prompts for it.
                        if(needsPrompt(res)) return asPreflight(res);

                        const thenClose = saving.intent === "close";
                        setSaving(null);
                        if(thenClose && !app.hasUnsavedChangesIn(saving.which)) leave();
                        return null;
                    }}
                />
            )}

            {globalSaving && app.editGlobal && (
                <GlobalDoorSaveDialog
                    app={app}
                    ddl={app.editGlobal.ddl}
                    name={app.editGlobal.name}
                    onCancel={() => setGlobalSaving(null)}
                    onSaved={() => {
                        const thenClose = globalSaving.thenClose;
                        setGlobalSaving(null);
                        if(thenClose && !app.hasUnsavedChanges()) leave();
                    }}
                />
            )}

            {save.error && showSaveError && (
                <ShowError message={save.error} onDone={() => setShowSaveError(false)} />
            )}

            {app && hardwareReconcile && hardwareReconcile.changed && hardwareReconcile !== ackedReconcile && (
                <HardwareReconcileDialog
                    report={hardwareReconcile}
                    hardwareGroup={app.hardwareGroup}
                    onClose={() => setAckedReconcile(hardwareReconcile)}
                />
            )}

            {app && secondaryOf && workspace === "screen" && (
                <SecondaryOpeningDialog
                    app={app}
                    secondaryOf={secondaryOf}
                    onWorkspaceChange={props.onWorkspaceChange}
                />
            )}

            {closing && (
                <ConfirmCloseDialog
                    onDismiss={() => setClosing(null)}
                    onDiscard={() => {
                        setClosing(null);
                        closing.exit();
                    }}
                    onSave={() => {
                        setClosing(null);
                        pendingExit.current = closing.exit;
                        if(app.editGlobal) {
                            setGlobalSaving({thenClose: true});
                            return;
                        }

                        // closing saves the workspace's elevation plus any other elevation
                        // with unsaved changes, so they aren't dropped on the way out.
                        startSave(unionSelection(workspaceSelection(props.workspace), app.unsavedSelection()), true);
                    }}
                />
            )}

            <Box sx={{
                display: "flex",
                alignItems: "stretch",
                height: 84,
                px: 1,
                py: 0.5,
                gap: 0.5,
                borderTop: 1,
                borderColor: "divider",
                overflowX: "auto",
                overflowY: "hidden",
            }}>
                {pick ? <StickPickTab app={app} pick={pick} />
                    : anchorSetup ? <AnchorSetupTab app={app} setup={anchorSetup} />
                    : effectiveTab === "selected" ? <SelectedTab app={app} />
                    : effectiveTab === "debug" ? <DebugTab app={app} />
                    : effectiveTab === "legacy" ? <LegacyTab app={app} />
                    : <WorkspaceTab app={app} />}
            </Box>
        </Paper>
    );
}

const ellipsis = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
} as const;

// one elevation's name on the top bar, as a one-letter prefix plus the name, so all
// three fit beside the opening name. "—" when that elevation isn't saved/named yet.
function ElevationName(props: {label: string; title: string; name: string | null}) {
    const dim = "rgba(255,255,255,0.6)";
    const strong = "rgba(255,255,255,0.92)";

    return (
        <Tooltip title={`${props.title}: ${props.name ?? "not saved"}`}>
            <Box component="span" sx={{display: "flex", gap: 0.25, minWidth: 0}}>
                <Box component="span" sx={{color: dim}}>{props.label}</Box>
                <Box component="span" sx={{color: props.name ? strong : dim, ...ellipsis, maxWidth: 120}}>
                    {props.name ?? "—"}
                </Box>
            </Box>
        </Tooltip>
    );
}

// the contextual tab label. A single element names its kind; a multi-selection
// names the group with a count (they're always same-group — see selectItem).
export function selectionTitle(app: ElevationApp, selection: MdSelection[]): string {
    if(selection.length === 1) return singleTitle(app, selection[0]);

    const count = selection.length;
    switch(selectionGroupOf(selection[0].type)) {
        case "cell": return `${count} Cells`;
        case "stick": return `${count} Sticks`;
        case "door-lite": return `${count} Lites`;
        case "hardware-item": return `${count} Hardware`;
        case "hinge": return `${count} Hinges`;
        case "void": return `${count} Steps`;
        case "door-slot": return `${count} Door Slots`;
        case "anchor": return `${count} Anchors`;
    }
}

function singleTitle(app: ElevationApp, selection: MdSelection): string {
    switch(selection.type) {
        case "stick": return findStickById(app.frameSpec, pieceStickId(selection.id))?.airGap ? "Air Gap" : "Stick";
        case "lite": return "Glass";
        case "panel": return "Panel";
        case "door-lite": return "Lite";
        case "hardware-item": return "Hardware";
        case "hinge": return "Hinge";
        case "void": return "Step";
        case "door-slot": return "Door Slot";
        case "anchor-stick":
        case "anchor-section": return "Anchors";
    }
}

// the things a save can stop and ask about, from either a preflight or a save that came back
// without saving.
type SaveConflicts = {
    nameConflict?: {name: string; openings: string[]} | null;
    sharedIdentityConflict?: {openings: string[]} | null;
    dimensionChange?: DimensionChange | null;
    hingeGroupSplit?: HingeGroupSplit | null;
    sharedElevations?: SharedElevations | null;
    slotUnlink?: {openings: string[]} | null;
};

function needsPrompt(res: SaveConflicts): boolean {
    return !!(res.nameConflict || res.sharedIdentityConflict || res.dimensionChange || res.hingeGroupSplit || res.sharedElevations || res.slotUnlink);
}

// normalize a save's conflict payload to the preflight shape the save dialog renders.
function asPreflight(res: SaveConflicts): SavePreflight {
    return {
        nameConflict: res.nameConflict ?? null,
        sharedIdentityConflict: res.sharedIdentityConflict ?? null,
        dimensionChange: res.dimensionChange ?? null,
        hingeGroupSplit: res.hingeGroupSplit ?? null,
        sharedElevations: res.sharedElevations ?? null,
        slotUnlink: res.slotUnlink ?? null,
    };
}
